import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Sort } from '@angular/material/sort';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { PaginationData, PaginationRange } from 'tiime-material';

import { ApiCommonService } from '@api-services/api-common.service';
import { ApiAlertError } from '@decorators/api-alert-error';
import { SendContractType } from '@enums/send-contract-type.enum';
import { GI_API_HEADERS, GI_API_ROUTES } from '@gi/api/routes';
import { ContractStatus } from '@gi/enums/contract-status.enum';
import { Contract, ContractJson } from '@gi/models/contract';
import { ContractAnnexModel, ContractAnnexModelJson } from '@gi/models/contract-annex-model';
import { ContractReminder, ContractReminderJson } from '@gi/models/contract-reminder';
import { GiEmailAttachment } from '@gi/models/gi-email-attachment';
import { GiEmailTemplate, GiEmailTemplateJson } from '@gi/models/gi-email-template';
import { DateHelper, DateInput } from '@helpers/date.helper';
import { FiltersParams, HttpHelper } from '@helpers/http.helper';
import { EmailAttachment, EmailAttachmentJsonSerialized } from '@models/email-attachment';
import { EmailTemplateJson } from '@models/email-template';
import { GIFile, GIFileJson } from '@models/gi-file';
import { ModelOffer, ModelOfferJson } from '@models/model-offer';
import { SmsTemplate, SmsTemplateJson } from '@models/sms-template';
import { FileSaverData } from '@services/file.service';

/**
 * Contract Api Service.
 */
@Injectable({
  providedIn: 'root'
})
export class ContractApiService extends ApiCommonService<Contract> {
  resourceUrl = GI_API_ROUTES.contracts;
  companyResourceUrl = GI_API_ROUTES.companyContracts;

  fromJson(json: Partial<ContractJson>): Contract {
    return Contract.fromJson(json);
  }

  toJson(model: Contract): Partial<ContractJson> {
    return Contract.toJson(model);
  }

  @ApiAlertError()
  getContracts(
    range: PaginationRange,
    searchTerms: string,
    sort: Sort,
    filters: FiltersParams
  ): Observable<PaginationData<Contract>> {
    let params = HttpHelper.setQParam(searchTerms);
    params = HttpHelper.setSortParam(sort, params);

    if (filters) {
      params = HttpHelper.setParams(filters, params, false);
    }

    return this.getPaginated(range, params);
  }

  @ApiAlertError()
  searchContracts(searchTerms: string): Observable<Contract[]> {
    let params = HttpHelper.setQParam(searchTerms);

    return super.search({ params });
  }

  @ApiAlertError()
  getContract(contractId: number): Observable<Contract> {
    return this.getById(contractId);
  }

  @ApiAlertError()
  getCompanyContracts(companyId?: number): Observable<Contract[]> {
    let url = this.companyResourceUrl;

    if (companyId) {
      url = HttpHelper.replaceCompanyUrl(url, companyId);
    }

    return super.getAll(null, url);
  }

  @ApiAlertError()
  createContract(contract: Contract): Observable<Contract> {
    return this.create(contract);
  }

  @ApiAlertError()
  updateContract(contract: Contract): Observable<Contract> {
    return this.update(contract.id, contract);
  }

  @ApiAlertError()
  archiveContract(contractId: number, archived: boolean): Observable<Contract> {
    const url = `${this.resourceUrl}/${contractId}/status`;
    const body: Partial<ContractJson> & { archived: boolean } = {
      id: contractId,
      archived
    };

    const headers: HttpHeaders = new HttpHeaders({
      Accept: GI_API_HEADERS.contractStatusV2
    });

    return this.http.patch<ContractJson>(url, body, { headers }).pipe(map((json: ContractJson) => this.fromJson(json)));
  }

  @ApiAlertError()
  updateContractStatus(
    contractId: number,
    status: ContractStatus,
    lossReason?: string,
    lossComment?: string
  ): Observable<Contract> {
    const url = `${this.resourceUrl}/${contractId}/status`;
    const body: Partial<ContractJson> = {
      id: contractId,
      status,
      loss_reason: lossReason ?? undefined,
      loss_comment: lossComment ?? undefined
    };
    const headers: HttpHeaders = new HttpHeaders({
      Accept: GI_API_HEADERS.contractStatusV2
    });

    return this.http.patch<ContractJson>(url, body, { headers }).pipe(map((json: ContractJson) => this.fromJson(json)));
  }

  @ApiAlertError()
  updateContractInternalComment(contractId: number, comment: string): Observable<Contract> {
    const body: Partial<ContractJson> = {
      id: contractId,
      internal_comment: comment || null
    };

    return this.updatePartial(contractId, body);
  }

  @ApiAlertError()
  updateContractNextReminderDate(contractId: number, date: DateInput): Observable<Contract> {
    const url = `${this.resourceUrl}/${contractId}/next_reminder_date`;
    const body: Partial<ContractJson> = {
      id: contractId,
      next_reminder_date: DateHelper.format(date)
    };

    return this.http
      .patch<ContractJson>(url, body)
      .pipe(map((contractJson: ContractJson) => this.fromJson(contractJson)));
  }

  @ApiAlertError()
  getContractEmailTemplate(contractId: number, templateId: number): Observable<GiEmailTemplate> {
    const url = `${this.resourceUrl}/${contractId}/email_templates/${templateId}`;

    return this.http
      .get<GiEmailTemplateJson>(url)
      .pipe(map((json: Partial<GiEmailTemplateJson>) => GiEmailTemplate.fromJson(json)));
  }

  @ApiAlertError()
  getContractSmsTemplate(contractId: number, type: SendContractType): Observable<SmsTemplate> {
    const url = `${this.resourceUrl}/${contractId}/sms_template?type=${type}`;

    return this.http
      .get<SmsTemplateJson>(url)
      .pipe(map((json: Partial<SmsTemplateJson>) => SmsTemplate.fromJson(json)));
  }

  @ApiAlertError()
  sendContract(
    contractId: number,
    type: SendContractType,
    external: boolean,
    sendEmail: boolean,
    sendSms: boolean,
    emailTemplate?: GiEmailTemplate,
    smsTemplate?: SmsTemplate,
    contractFiles?: GiEmailAttachment[]
  ): Observable<void> {
    const url = `${this.resourceUrl}/${contractId}/send`;

    const body: {
      type: SendContractType;
      send_email: boolean;
      send_sms: boolean;
      email?: Partial<EmailTemplateJson>;
      sms?: Partial<EmailTemplateJson>;
      contract_files?: EmailAttachmentJsonSerialized[];
      reminder?: {
        external: boolean;
      };
    } = {
      type,
      send_email: sendEmail,
      send_sms: sendSms,
      email: emailTemplate ? GiEmailTemplate.toJson(emailTemplate) : undefined,
      sms: smsTemplate ? SmsTemplate.toJson(smsTemplate) : undefined,
      contract_files: contractFiles
        ? contractFiles.map((contractFile: EmailAttachment) => EmailAttachment.toJson(contractFile))
        : undefined,
      reminder: type === SendContractType.reminder ? { external } : undefined
    };

    return this.http.post<void>(url, body);
  }

  @ApiAlertError()
  downloadContractPdf(contractId: number): Observable<FileSaverData> {
    const url = `${this.resourceUrl}/${contractId}/pdf`;

    const blobObservable = this.http.get(url, {
      responseType: 'blob',
      observe: 'response'
    });

    return HttpHelper.getFileSaverData(blobObservable);
  }

  @ApiAlertError()
  downloadContractFile(contractId: number, fileId: number): Observable<FileSaverData> {
    const url = `${this.resourceUrl}/${contractId}/contract_files/${fileId}/file`;

    const blobObservable = this.http.get(url, {
      responseType: 'blob',
      observe: 'response'
    });

    return HttpHelper.getFileSaverData(blobObservable);
  }

  @ApiAlertError()
  getContractFilePreview(contractId: number, fileId: number): Observable<Blob> {
    const url = `${this.resourceUrl}/${contractId}/contract_files/${fileId}/preview`;

    return this.http.get(url, { responseType: 'blob' });
  }

  @ApiAlertError()
  addContractFile(contractId: number, file: File, title?: string): Observable<GIFile> {
    const url = `${this.resourceUrl}/${contractId}/contract_files`;
    const form = new FormData();
    form.set('file', file);
    if (title) {
      form.set('title', title);
    }
    const headers: HttpHeaders = new HttpHeaders({
      Accept: GI_API_HEADERS.contractFileTypeV2
    });

    return this.http.post<GIFileJson>(url, form, { headers }).pipe(map((json: GIFileJson) => GIFile.fromJson(json)));
  }

  @ApiAlertError()
  deleteContractFile(contractId: number, fileId: number): Observable<void> {
    const url = `${this.resourceUrl}/${contractId}/contract_files/${fileId}`;

    return this.http.delete<void>(url);
  }

  @ApiAlertError()
  getModelOffers(): Observable<ModelOffer[]> {
    let url = `${this.resourceUrl}/offer_models`;

    return this.http
      .get<ModelOfferJson[]>(url)
      .pipe(map((jsons: ModelOfferJson[]) => jsons.map((json: ModelOfferJson) => ModelOffer.fromJson(json))));
  }

  @ApiAlertError()
  getAnnexModels(): Observable<ContractAnnexModel[]> {
    let url = `${this.resourceUrl}/contract_annex_models`;

    return this.http
      .get<ContractAnnexModelJson[]>(url)
      .pipe(
        map((jsons: ContractAnnexModelJson[]) =>
          jsons.map((json: ContractAnnexModelJson) => ContractAnnexModel.fromJson(json))
        )
      );
  }

  @ApiAlertError()
  getReminders(contractId: number): Observable<ContractReminder[]> {
    let url = `${this.resourceUrl}/${contractId}/reminders`;

    return this.http
      .get<ContractReminderJson[]>(url)
      .pipe(
        map((jsons: ContractReminderJson[]) =>
          jsons.map((json: ContractReminderJson) => ContractReminder.fromJson(json))
        )
      );
  }

  @ApiAlertError()
  getBrandLogoFilePreview(logoFileId: string): Observable<Blob> {
    const url = `${this.resourceUrl}/brand_logo_file/${logoFileId}/preview`;

    return this.http.get(url, { responseType: 'blob' });
  }
}
